import has from 'lodash/has';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import { isTrue } from '@/utils/boolean';

const cellEditing = {
  isModified: false,
  isEditing: false, // true면 Editor활성화 상태, false면 비활성화 상태.
  data: null, // 편집할 데이터
  property: null, // 편집할 데이터 필드
  value: null, // 편집할 값
  originalValue: null, // 편집할 값의 기존 값
};

/**
 * 단일 편집 Mixin
 * @type {Object}
 */
export default {
  data() {
    return {
      cellEditing: clone(cellEditing),
    };
  },
  methods: {
    /**
     * 셀 편집 상태를 초기화한다.
     * @author Ju
     */
    resetCellEditing() {
      this.cellEditing = clone(cellEditing);
    },
    /**
     * 편집을 시작하기 위해 상태 값을 설정하여 에디터를 활성화한다.
     * @author Ju
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     */
    startEdit(row, column) {
      if (!has(row, 'editing')) {
        return;
      }
      console.debug('-- start direct', row, column.property, this.cellEditing.isEditing, this.cellEditing.data, this.cellEditing.property, this.cellEditing);

      // 이미 편집 상태인 셀이 있는 경우 편집을 취소한다. (이미 동일 대상에 대한 Editor가 활성화된 경우 중복 설정을 하지않는다.)
      if (this.cellEditing.isEditing && ((!isNil(this.cellEditing.data) && this.cellEditing.data !== row) || this.cellEditing.property !== column.property)) {
        this.cancelEdit();
      }

      if (!this.cellEditing.isEditing) {
        const property = column.property;
        this.cellEditing.data = row;
        this.cellEditing.data.editing = true;
        this.cellEditing.property = property;
        this.cellEditing.originalValue = row[property];
        this.cellEditing.isEditing = true;

        if (row.modified && row.modified[property] !== undefined) {
          this.cellEditing.originalValue = row.modified[property];
        } else {
          this.cellEditing.originalValue = row[property];
        }

        this.handlerStartEdit(row, property);
      }
    },
    /**
     * 데이터 편집을 종료한다.
     * 값 변경 여부 확인 후 dirty 및 modified에 변경된 필드의 기존 값 설정
     * @param  {?} newValue [변경된 값]
     */
    endEdit(newValue) {
      console.debug('-- end direct', newValue, this.cellEditing.data, this.cellEditing.property, this.cellEditing);
      const data = this.cellEditing.data;
      const property = this.cellEditing.property;

      if (data && property) {
        // TODO: Ediotr의 keyup 이벤트로 endEdit가 호출되는 경우, keyup 이벤트에 endEdit 한번 호출,
        // blur 이벤트에서 한번 호출 총 2번 호출이 가서 2번째에서 editing 객체가 초기화되어 에러남.
        // 일단  data와 property가 있을 경우만 하단의 로직 수행하도록 처리함.
        const dirty = this.setModified(data, property, this.cellEditing.originalValue, newValue);
        this.completeEdit(data, property, dirty);
      }
    },
    /**
     * 두 상태 값의 셀에 대한 편집을 한다.
     * (ex) checkbox, swithc
     * @author Ju
     * @param  {Object} data     데이터 객체
     * @param  {String} property 편집할 필드
     * @param  {?} newValue 편집한 값
     * @param  {Boolean} rawValue 편집한 상태 값을 true or false로 치환한 데이터
     */
    editTwoStatusCell(data, property, newValue, rawValue) {
      console.debug('-- edit direct', data, property, newValue);
      let originalValue;
      if (data.modified && data.modified[property] !== undefined) {
        originalValue = data.modified[property];
      } else {
        originalValue = data[property];
      }

      let modifiedValue;
      if (!isTrue(originalValue) && !rawValue) {
        modifiedValue = originalValue;
      } else {
        modifiedValue = newValue;
      }

      const dirty = this.setModified(data, property, originalValue, modifiedValue);
      this.completeEdit(data, property, dirty);
    },
    /**
     * 셀 데이터를 바로 편집한다.
     * @author Ju
     * @param  {Object} data     데이터 객체
     * @param  {String} property 편집할 필드
     * @param  {?} newValue 편집한 값
     */
    editDirect(data, property, newValue) {
      console.debug('-- edit direct', data, property, newValue);
      let originalValue;
      if (data.modified && data.modified[property] !== undefined) {
        originalValue = data.modified[property];
      } else {
        originalValue = data[property];
      }

      const dirty = this.setModified(data, property, originalValue, newValue);
      this.completeEdit(data, property, dirty);
    },
    editDirectBulk(tableData, property, newValue) {
      console.debug('-- edit direct Bulk', tableData, property, newValue);
      const modfiedTableData = cloneDeep(tableData);

      let isModified = false;
      forEach(modfiedTableData, (data) => {
        let originalValue;
        if (data.modified && data.modified[property] !== undefined) {
          originalValue = data.modified[property];
        } else {
          originalValue = data[property];
        }

        this.setModified(data, property, originalValue, newValue);
        this.checkDirty(data);
        if (!isModified && data.dirty) {
          isModified = true;
        }
        data.editing = false;
      });
      /* eslint-disable no-param-reassign */
      tableData = modfiedTableData;
      /* eslint-enable  no-param-reassign */

      return { tableData: modfiedTableData, property, dirty: isModified };
    },
    completeEditBulk(tableData, property, dirty) {
      this.cellEditing = {
        isModified: this.cellEditing.isModified,
        data: null,
        property: null,
        originalValue: null,
        isEditing: false,
      };
      this.handlerEndEdit(null, property, dirty);
    },
    /**
     * 셀 편집을 완료한다.
     * @author Ju
     * @param  {Object} data     데이터 객체
     * @param  {String} property 편집할 필드
     * @param  {[type]} dirty    변경 여부 (true면 해당 필드에 대해 값이 변경됨, false면 변경되지 않음)
     */
    completeEdit(data, property, dirty) {
      data.editing = false;
      this.cellEditing = {
        isModified: this.cellEditing.isModified,
        data: null,
        property: null,
        originalValue: null,
        isEditing: false,
      };

      this.checkDirty(data);
      this.handlerEndEdit(data, property, dirty);
    },
    /**
     * 셀 편집을 취소한다.
     * @author Ju
     */
    cancelEdit() {
      console.debug('-- cancel direct', this.cellEditing.data, this.cellEditing.property, this.cellEditing);
      const property = this.cellEditing.property;
      this.cellEditing.data[property] = this.cellEditing.originalValue;
      this.cellEditing.data.editing = false;
      this.cellEditing.isEditing = false;
    },
    /**
     * 편집된 데이터에 대해 modfied 객체를 설정하고 편집을 종료한다.
     * @author Ju
     * @param  {Object} data     데이터 객체
     * @param  {String} property 편집할 필드
     * @param  {?} originalValue 원본 값
     * @param  {?} newValue 편집한 값
     */
    setModified(data, property, originalValue, newValue) {
      data[property] = newValue;
      let dirty;
      if (originalValue === newValue || ((isNil(originalValue) || originalValue === '') && (isNil(newValue) || newValue === ''))) {
        // 변경 x
        if (has(data, 'modified') && !isUndefined(data.modified[property])) {
          data.modified[property] = undefined;
        }
        dirty = false;
      } else {
        // 변경
        if (!has(data, 'modified')) {
          data.modified = {};
        }
        data.modified[property] = originalValue;
        dirty = true;
      }
      return dirty;
    },
    /**
     * 데이터의 변경 여부를 체크하고 설정한다.
     * @author Ju
     * @param  {Object} data     데이터 객체
     */
    checkDirty(data) {
      const values = Object.values(data.modified);
      let dirty = false;
      for (let i = 0; i < values.length; i += 1) {
        if (!isUndefined(values[i])) {
          dirty = true;
          break;
        }
      }
      data.dirty = dirty;
    },
    /**
     * 테이블 데이터에 변경된 데이터 여부를 체크하고 반환한다.
     * 변경된 데이터가 있을 경우 true, 없을 경우 false를 반환한다.
     * @author Ju
     * @param  {Array} tableData 테이블 데이터
     * @return {Boolean}      변경 여부
     */
    checkExistModifiedData(tableData) {
      let flag = false;
      if (!isEmpty(tableData)) {
        for (let i = 0; i < tableData.length; i += 1) {
          if (tableData[i].dirty) {
            flag = true;
            break;
          }
        }
      }
      this.cellEditing.isModified = flag;
      return flag;
    },
    /**
     * 편집 가능한 테이블 데이터를 가공하여 반환한다.
     * @author Ju
     * @param  {Array} tableData    테이블 데이터
     * @param  {[type]} columns [description]
     * @return {Array}         편집을 위한 필드와 값이 설정된 테이블 데이터
     */
    getEditableTableData(_tableData, _columns) {
      let tableData;
      const editableColumnProps = [];
      _columns.forEach((column) => {
        if (column.editable && column.prop) {
          editableColumnProps.push(column.prop);
        }
      });

      // 편집을 위한 필드 설정
      // 1. modified 객체 추가 (undefined 값을 가지는 editable 컬럼 필드들을 가지는 객체)
      // 2. editing 필드 추가 (초기 값은 false) → editor 활성화 여부 판단에 사용.
      if (!isEmpty(editableColumnProps)) {
        tableData = cloneDeep(_tableData);
        forEach(tableData, (item) => {
          if (!has(item, 'modified')) {
            const modified = {};
            forEach(editableColumnProps, (column) => {
              modified[column] = undefined;
              if (!has(item, column) || isUndefined(item[column])) {
                item[column] = null; // 편집 가능한 필드에 데이터가 없는 경우 modified의 undefined 상태와 구분하기 위해 null을 설정해줌
              }
            });
            item.modified = modified;
          }
          item.editing = false;
        });
      } else {
        tableData = _tableData;
      }
      return tableData;
    },
    /**
     * 파라미터로 전달된 테이블 데이터에서 수정된 데이터에 대한 배열을 반환한다.
     * @author Ju
     * @param  {Array} tableData     테이블 데이터
     * @param  {Boolean} allFields 편집 가능한 모든 필드 포함 여부 (기본은 false이며 편집된 필드만 반환함.)
     * @return {Array}           수정된 데이터 배열
     */
    getUpdatedDatas(tableData, allFields, includeFields) {
      const updatedData = [];
      if (allFields) {
        forEach(tableData, (item) => {
          if (item.dirty) {
            const updatedItem = {};
            forEach(item.modified, (value, key) => {
              updatedItem[key] = item[key];
            });
            forEach(includeFields, (field) => {
              updatedItem[field] = item[field];
            });
            updatedData.push(updatedItem);
          }
        });
      } else {
        forEach(tableData, (item) => {
          if (item.dirty) {
            const updatedItem = {};
            forEach(item.modified, (value, key) => {
              if (!isUndefined(value)) {
                updatedItem[key] = item[key];
              }
            });
            forEach(includeFields, (field) => {
              updatedItem[field] = item[field];
            });
            updatedData.push(updatedItem);
          }
        });
      }
      return updatedData;
    },
    /* eslint-disable no-unused-vars */
    /**
     * 셀 편집 시작에 대한 핸들러
     * 셀 편집 시작 시 커스텀하게 로직을 추가하기 위해 사용.
     * @author Ju
     * @param  {[type]} data     [description]
     * @param  {[type]} property [description]
     */
    handlerStartEdit(data, property) {}, // for override
    /**
     * 셀 편집 종료 대한 핸들러
     * 셀 편집 종료 시 커스텀하게 로직을 추가하기 위해 사용.
     * @author Ju
     * @param  {[type]} data     [description]
     * @param  {[type]} property [description]
     */
    handlerEndEdit(data, property, dirty) {}, // for override
    /* eslint-enable no-unused-vars */
  },
};



// WEBPACK FOOTER //
// ./src/mixins/table/cell-editing.js